import { ActionIcon, Button, FileButton, Loader, Stack } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCamera, IconFileTypePdf, IconPhoto, IconX } from '@tabler/icons-react'
import { fileTypes, fileUrl, MAX_FILE_BYTES } from '@tnn/core'
import type { Task, TaskResultInput } from '@tnn/domain'
import imageCompression from 'browser-image-compression'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api, isApiError } from '../api/client'
import { uploadSignature } from '../signature/api'
import { type NewSignature, SignatureCapture } from '../signature/capture'
import { tasksApi } from './api'
import classes from './work.module.css'

/**
 * Doing a task on the device that does it: photos or a file taken or chosen and uploaded on the spot, or a signature,
 * then handed in — on one's phone in Thananan OS (งานรอทำ), or on one's portable screen (its own upload and hand-in,
 * `HandIn`).
 */

/** Where the files go and how the task is handed in */
export interface HandIn {
  /** `POST /api<uploadPath>`: none for the signed-in person's `/files` */
  uploadPath?: string
  done: (id: string, input: TaskResultInput) => Promise<Task>
}

const defaultHandIn: HandIn = { done: tasksApi.done }

export interface WorkProps {
  task: Task
  onDone: (task: Task) => void
  handIn?: HandIn
}

/** A file picked on the device: uploading, uploaded (its id), or failed */
interface Picked {
  key: string
  name: string
  preview: string | null
  fileId?: string
  error?: string
}

/** Photos are made smaller on the device before they go: quick on a weak signal, and plenty for a slip or a document */
async function shrink(file: File): Promise<File> {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size < 600 * 1024) return file
  const smaller = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 2000, useWebWorker: true, initialQuality: 0.85 })
  return new File([smaller], file.name, { type: smaller.type || file.type })
}

/** Photos or a file: taken with the camera or chosen, each uploaded on the spot, then handed in */
export function FilesWork({ task, onDone, handIn = defaultHandIn }: WorkProps) {
  const { t } = useTranslation()
  const many = task.kind === 'files'
  const [picked, setPicked] = useState<Picked[]>([])
  const [busy, setBusy] = useState(false)
  // Previews live as long as the page
  const previews = useRef<string[]>([])
  useEffect(
    () => () => {
      for (const url of previews.current) URL.revokeObjectURL(url)
    },
    [],
  )
  const preview = (file: File) => {
    if (!file.type.startsWith('image/') || file.type === 'image/heic' || file.type === 'image/heif') return null
    const url = URL.createObjectURL(file)
    previews.current.push(url)
    return url
  }

  const add = async (files: File[]) => {
    const chosen = many ? files : files.slice(0, 1)
    const items = chosen.map((file) => ({ key: crypto.randomUUID(), name: file.name, preview: preview(file) }))
    setPicked((was) => (many ? [...was, ...items] : items))
    await Promise.all(
      chosen.map(async (file, i) => {
        const key = items[i]?.key
        const set = (change: Partial<Picked>) => setPicked((was) => was.map((p) => (p.key === key ? { ...p, ...change } : p)))
        try {
          const ready = await shrink(file)
          if (ready.size > MAX_FILE_BYTES) return set({ error: t('file.tooBig') })
          set({ fileId: (await api.upload(ready, handIn.uploadPath)).id })
        } catch (err) {
          set({ error: isApiError(err) && err.message !== 'network' ? err.message : t('file.failed') })
        }
      }),
    )
  }
  const ready = picked.filter((p) => p.fileId)
  const uploading = picked.some((p) => !p.fileId && !p.error)
  const send = async () => {
    setBusy(true)
    try {
      onDone(await handIn.done(task.id, { fileIds: ready.map((p) => p.fileId as string) }))
    } catch (err) {
      notifications.show({ color: 'red', message: isApiError(err) ? err.message : t('errors.generic') })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Stack gap="md">
      <div className={classes.pickers}>
        <FileButton onChange={(file) => file && add([file])} accept="image/*" capture="environment">
          {(props) => (
            <Button {...props} size="lg" leftSection={<IconCamera size={22} />}>
              {t('task.work.camera')}
            </Button>
          )}
        </FileButton>
        <FileButton onChange={(files) => add(files)} accept={fileTypes.join(',')} multiple>
          {(props) => (
            <Button {...props} size="lg" variant="default" leftSection={<IconPhoto size={22} />}>
              {t('task.work.choose')}
            </Button>
          )}
        </FileButton>
      </div>
      {picked.length > 0 && (
        <div className={classes.thumbs}>
          {picked.map((p) => (
            <div key={p.key} className={classes.thumb} data-error={p.error ? true : undefined}>
              {p.preview ? (
                <img src={p.preview} alt="" />
              ) : p.fileId ? (
                <img src={fileUrl(p.fileId)} alt="" />
              ) : (
                <IconFileTypePdf size={28} />
              )}
              {!p.fileId && !p.error && (
                <span className={classes.uploading}>
                  <Loader size="sm" />
                </span>
              )}
              {p.error && <span className={classes.failed}>{p.error}</span>}
              <ActionIcon
                className={classes.remove}
                size={28}
                radius="xl"
                variant="filled"
                color="dark"
                aria-label={t('file.remove')}
                onClick={() => setPicked((was) => was.filter((x) => x.key !== p.key))}
              >
                <IconX size={16} />
              </ActionIcon>
            </div>
          ))}
        </div>
      )}
      <Button size="lg" onClick={send} loading={busy} disabled={!ready.length || uploading}>
        {t('task.work.send', { count: ready.length })}
      </Button>
    </Stack>
  )
}

/** A signature: drawn with a finger or a photo of it on paper, handed in as its person's own */
export function SignatureWork({ task, onDone, handIn = defaultHandIn }: WorkProps) {
  const { t } = useTranslation()
  const [signature, setSignature] = useState<NewSignature | null>(null)
  const [busy, setBusy] = useState(false)
  const send = async () => {
    if (!signature) return
    setBusy(true)
    try {
      const fileId = await uploadSignature(signature, handIn.uploadPath)
      onDone(await handIn.done(task.id, { fileIds: [fileId], signatureMethod: signature.method }))
    } catch (err) {
      notifications.show({ color: 'red', message: isApiError(err) ? err.message : t('signature.failed') })
    } finally {
      setBusy(false)
    }
  }
  return (
    <Stack gap="md">
      <SignatureCapture onChange={setSignature} />
      <Button size="lg" onClick={send} loading={busy} disabled={!signature}>
        {t('task.work.sendSignature')}
      </Button>
    </Stack>
  )
}
